import { useRef, useState } from 'react';
import { useSession, useUser } from '@clerk/react';
import type { EmailAddressResource } from '@clerk/types';
import { advisorsApi } from '@/api/waterlily';

/**
 * HIPAA-gated self-serve email change.
 *
 * Reverification happens FIRST: we email a code to the user's CURRENT (old) primary email and
 * require them to enter it before any change. Only then do we add + verify the new email, promote
 * it to primary, and sync our DB. Running reverification before any mutation guarantees the
 * challenge targets the old address and that the side-effecting steps run exactly once.
 */

export type ChangeEmailStep = 'idle' | 'reverify' | 'newEmail' | 'verifyNewEmail' | 'done';

function clerkErrorMessage(err: unknown, fallback: string): string {
  const e = err as { errors?: Array<{ longMessage?: string; message?: string }>; message?: string };
  return e?.errors?.[0]?.longMessage || e?.errors?.[0]?.message || e?.message || fallback;
}

interface UseChangeEmailReturn {
  step: ChangeEmailStep;
  isSubmitting: boolean;
  error: string | null;
  newEmail: string;
  /** Begin the flow: send a code to the current (old) email. */
  begin: () => Promise<void>;
  /** Submit the code sent to the old email. */
  submitReverification: (code: string) => Promise<void>;
  /** Submit the desired new email; sends a verification code to that new address. */
  submitNewEmail: (email: string) => Promise<void>;
  /** Submit the code sent to the new address; promotes it and syncs the DB. */
  submitNewEmailCode: (code: string) => Promise<void>;
  /** Resend the code for the current step. */
  resend: () => Promise<void>;
  reset: () => void;
}

export function useChangeEmail(
  advisorId: string | undefined,
  onSuccess?: (newEmail: string) => void,
): UseChangeEmailReturn {
  const { session } = useSession();
  const { user } = useUser();

  const [step, setStep] = useState<ChangeEmailStep>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');

  // The pending new-email resource, held between "submitNewEmail" and "submitNewEmailCode".
  const newEmailResourceRef = useRef<EmailAddressResource | null>(null);
  // The email address id Clerk offered for old-email reverification (used for resend).
  const reverifyEmailAddressIdRef = useRef<string | null>(null);

  const run = async (fallback: string, fn: () => Promise<void>) => {
    setError(null);
    setIsSubmitting(true);
    try {
      await fn();
    } catch (err) {
      setError(clerkErrorMessage(err, fallback));
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const begin = () =>
    run('Could not start verification. Please try again.', async () => {
      if (!session) throw new Error('Not signed in.');

      // Prove control of the OLD/current primary email by sending a code to it. Use the strategy
      // Clerk actually offers so we fail loudly if "Email verification code" isn't enabled.
      const verification = await session.startVerification({ level: 'first_factor' });
      const emailFactor = (verification.supportedFirstFactors ?? []).find(
        f => f.strategy === 'email_code',
      );
      if (!emailFactor) {
        throw new Error(
          'Email verification isn’t enabled for your account, so we can’t confirm your ' +
            'current email. Please contact support to change your email.',
        );
      }
      reverifyEmailAddressIdRef.current = emailFactor.emailAddressId;
      await session.prepareFirstFactorVerification({
        strategy: 'email_code',
        emailAddressId: emailFactor.emailAddressId,
      });
      setStep('reverify');
    });

  const submitReverification = (code: string) =>
    run('That code was incorrect or expired. Please try again.', async () => {
      if (!session) throw new Error('Not signed in.');
      const result = await session.attemptFirstFactorVerification({ strategy: 'email_code', code });
      if (result.status !== 'complete') {
        throw new Error('Verification is not complete.');
      }
      setStep('newEmail');
    });

  // Promote a now-verified address to primary, remove every other email so the account is left with
  // exactly one, and sync our DB. Shared by the code-entry path and the already-verified shortcut.
  const finalizeNewEmail = async (resource: EmailAddressResource) => {
    if (!user) throw new Error('Not signed in.');

    // Promote the new address to primary. We deliberately DON'T delete the old emails here: deleting
    // an email is a Clerk "sensitive action" that requires fresh reverification, so client-side
    // destroy() calls were failing silently. The Clerk `user.updated` webhook removes the now-stale
    // addresses server-side via the secret-key Backend API (not gated by reverification) — see
    // postClerkWebhookForPublic. Old verified emails remain valid login identifiers until it runs.
    await user.update({ primaryEmailAddressId: resource.id });

    if (advisorId) {
      // Best-effort fast path to update our DB now. The server live-fetches the primary from Clerk
      // (so it doesn't depend on the token's email claim), and the Clerk `user.updated` webhook is
      // the authoritative sync that reconciles anything this misses — so a failure here must NOT
      // fail the flow or surface an error to the user.
      try {
        await advisorsApi.updateEmail(advisorId, resource.emailAddress);
      } catch (err) {
        console.warn('In-band email sync failed; the Clerk webhook will reconcile it.', err);
      }
    }
    onSuccess?.(resource.emailAddress);
    setStep('done');
  };

  const submitNewEmail = (email: string) =>
    run('Could not add that email. Please try again.', async () => {
      if (!user) throw new Error('Not signed in.');
      const trimmed = email.trim().toLowerCase();
      if (!trimmed) throw new Error('Please enter an email address.');

      const currentPrimary = user.primaryEmailAddress?.emailAddress.toLowerCase();
      if (trimmed === currentPrimary) {
        throw new Error('That’s already your current email.');
      }

      // Reuse an address that's already on the account (e.g. an unverified leftover from an earlier
      // abandoned attempt) instead of re-creating it — createEmailAddress would reject a duplicate.
      let resource = user.emailAddresses.find(e => e.emailAddress.toLowerCase() === trimmed);
      if (!resource) {
        resource = await user.createEmailAddress({ email: trimmed });
      }

      // Already verified on this account (a secondary verified address): there's nothing to verify,
      // so skip the code step entirely and promote it straight away. Attempting verification on an
      // already-verified address would error ("This verification has already been verified").
      setNewEmail(trimmed);
      if (resource.verification?.status === 'verified') {
        await finalizeNewEmail(resource);
        return;
      }

      await resource.prepareVerification({ strategy: 'email_code' });
      newEmailResourceRef.current = resource;
      setStep('verifyNewEmail');
    });

  const submitNewEmailCode = (code: string) =>
    run('That code was incorrect or expired. Please try again.', async () => {
      if (!user || !session) throw new Error('Not signed in.');
      const resource = newEmailResourceRef.current;
      if (!resource) throw new Error('No pending email to verify.');

      const verified = await resource.attemptVerification({ code });
      if (verified.verification.status !== 'verified') {
        throw new Error('New email verification is not complete.');
      }

      await finalizeNewEmail(resource);
    });

  const resend = () =>
    run('Could not resend the code. Please try again.', async () => {
      if (step === 'reverify' && session && reverifyEmailAddressIdRef.current) {
        await session.prepareFirstFactorVerification({
          strategy: 'email_code',
          emailAddressId: reverifyEmailAddressIdRef.current,
        });
      } else if (step === 'verifyNewEmail' && newEmailResourceRef.current) {
        await newEmailResourceRef.current.prepareVerification({ strategy: 'email_code' });
      }
    });

  const reset = () => {
    newEmailResourceRef.current = null;
    setStep('idle');
    setIsSubmitting(false);
    setError(null);
    setNewEmail('');
  };

  return {
    step,
    isSubmitting,
    error,
    newEmail,
    begin,
    submitReverification,
    submitNewEmail,
    submitNewEmailCode,
    resend,
    reset,
  };
}
